import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Chart of Accounts — stage 1 of the accounting front end (2 October 2026).
// Three views on one book: the chart, the import that loads it (mig 342's importer — this screen
// uploads, shows the verdict row by row, and applies only when told to), and the mapping of
// suppliers, payees, buyers and Deroi vendors to ledger accounts.
//
// THE EMPTY STATE IS THE FIRST THING AN ACCOUNTANT SEES, so it is written as the normal case: no
// chart, nothing mapped. It says what is missing and what to do, and never renders as a blank
// table. Unmapped parties read as a to-do list, not as errors.
//
// Every refusal is the server's own sentence, shown where the act was attempted. Every control
// that cannot be used says why beside it.
// ─────────────────────────────────────────────────────────────────────────────

const C = { ink: '#1a1a2e', muted: '#777', line: '#E8E7E7', brand: '#3B3A8F', brandBg: '#EEEDF8',
  todo: '#8A6D1F', todoBg: '#FFF8E6', ok: '#166534', okBg: '#EAF7EE', bad: '#A32D2D', badBg: '#FCEBEB' };
const card = { background: 'white', borderRadius: 10, padding: '14px 18px', border: `1px solid ${C.line}` };
const th = { padding: '7px 9px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '7px 9px', fontSize: 12, borderBottom: '1px solid #f3f3f3', verticalAlign: 'top' };
const btn = { padding: '7px 14px', background: C.brand, color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
const btnGhost = { ...btn, background: C.brandBg, color: C.brand };
const off = (b) => ({ ...b, background: '#e5e5e5', color: '#999', cursor: 'not-allowed' });
const input = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const chip = (bg, c) => ({ display: 'inline-block', padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: bg, color: c, whiteSpace: 'nowrap' });
const why = { fontSize: 11, color: C.muted, marginLeft: 8 };
const fmtDT = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const errText = (e) => e?.response?.data?.error || e?.message || 'no reason was given';

function Notice({ tone = 'info', children }) {
  const t = { info: [C.brandBg, C.brand], todo: [C.todoBg, C.todo], bad: [C.badBg, C.bad], ok: [C.okBg, C.ok] }[tone];
  return <div style={{ background: t[0], color: t[1], borderRadius: 8, padding: '10px 14px', fontSize: 12, lineHeight: 1.55, marginBottom: 12 }}>{children}</div>;
}

export default function ChartOfAccounts() {
  const [books, setBooks] = useState({ data: [], can_switch: false, can_change: false, can_apply: false, apply_refusal: null });
  const [company, setCompany] = useState('');
  const [view, setView] = useState('chart');
  const [chart, setChart] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);

  useEffect(() => {
    api.get('/finance/accounting/books')
      .then(r => { setBooks(r.data); setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || ''); })
      .catch(e => setLoadErr(`Could not find which books you can open: ${errText(e)}`));
  }, []);

  const loadChart = useCallback(() => {
    if (!company) return;
    setLoadErr(null);
    api.get('/finance/accounting/chart', H()).then(r => setChart(r.data))
      .catch(e => { setChart(null); setLoadErr(`Could not read the chart: ${errText(e)}`); });
  }, [company, H]);
  useEffect(() => { loadChart(); }, [loadChart]);

  const bookName = (books.data.find(b => b.id === company) || {}).name || 'this book';
  const canChange = !!books.can_change;
  const st = chart?.state;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#888' }}>Book</span>
          <select value={company} disabled={!books.can_switch} onChange={e => setCompany(e.target.value)}
            style={{ ...input, fontWeight: 700, color: C.brand, background: books.can_switch ? C.brandBg : '#f5f5f5' }}>
            {books.data.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {!books.can_switch && <span style={why}>Your account keeps only its own company's book.</span>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['chart', '📒 Chart'], ['import', '⬆️ Import'], ['mapping', '🔗 Party mapping']].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)}
              style={{ ...(view === k ? btn : btnGhost), padding: '6px 12px' }}>{l}</button>
          ))}
        </div>
      </div>

      {!canChange && (
        <Notice>
          You can read the chart, the uploads and the mapping, but not change them. Management approves the payments
          that will be posted against these accounts, so it does not also shape them — accounts or admin makes changes here.
        </Notice>
      )}
      {loadErr && <Notice tone="bad">{loadErr}</Notice>}

      {view === 'chart' && <ChartView chart={chart} bookName={bookName} canChange={canChange} goImport={() => setView('import')} />}
      {view === 'import' && company && <ImportView H={H} company={company} bookName={bookName} canChange={canChange} applyRefusal={books.apply_refusal} onApplied={loadChart} hasChart={!!st?.accounts} />}
      {view === 'mapping' && company && <MappingView H={H} company={company} bookName={bookName} canChange={canChange} goImport={() => setView('import')} />}
    </div>
  );
}

// ── THE CHART ────────────────────────────────────────────────────────────────────────────────
function ChartView({ chart, bookName, canChange, goImport }) {
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');
  if (!chart) return <div style={{ ...card, color: C.muted, fontSize: 12 }}>Reading the chart…</div>;
  const st = chart.state;

  if (!st.accounts) {
    return (
      <div style={{ ...card, maxWidth: 820 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 6 }}>No chart of accounts is loaded for {bookName} yet.</div>
        <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>
          That is the expected starting point, not a fault. Until a chart is loaded, nothing in Khyati can be posted to a
          ledger, and suppliers, payees and buyers have no accounts to be mapped to. Busy remains the books of record.
        </div>
        {st.batches_waiting > 0 && <Notice tone="todo"><b>An upload has been checked and is waiting to be applied.</b> Open Import to review it and apply it.</Notice>}
        {st.batches_rejected > 0 && !st.batches_waiting && <Notice tone="todo">The last upload for this book had rows that could not be loaded, so nothing was applied. Open Import to see each row and its reason.</Notice>}
        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, margin: '14px 0 6px' }}>What to do</div>
        <ol style={{ fontSize: 13, color: '#444', lineHeight: 1.7, margin: 0, paddingLeft: 20 }}>
          <li>From Busy, export the chart of accounts for <b>{bookName} only</b> (never both companies in one file) — every ledger, whether it has a balance or not.</li>
          <li>Save it as CSV or Excel, data on the first sheet, one header row. Columns: <b>Ledger Name</b>, <b>Parent Group</b>, <b>Nature</b> (Asset / Liability / Income / Expense / Capital) and <b>Account Code</b> — a code every ledger has, never blank and never repeated. Which Busy column supplies it is still being checked, so confirm before exporting. GSTIN and PAN are optional. Leave group lines out — groups are built from the Parent Group column.</li>
          <li>Upload it under Import. Every row is checked first; you see each one that cannot be loaded and why, and <b>nothing is applied until you say so</b>.</li>
        </ol>
        <div style={{ marginTop: 14 }}>
          {canChange ? <button style={btn} onClick={goImport}>Go to Import</button>
            : <span style={{ fontSize: 12, color: C.muted }}>Accounts or admin loads the chart.</span>}
        </div>
      </div>
    );
  }

  const groups = Object.fromEntries(chart.groups.map(g => [g.id, g]));
  const needle = q.trim().toLowerCase();
  const visible = chart.accounts.filter(a =>
    (show === 'all' || (show === 'unmapped' && a.is_active && !a.mapped_count) || (show === 'retired' && !a.is_active))
    && (!needle || [a.code, a.name, groups[a.group_id]?.name].some(x => String(x || '').toLowerCase().includes(needle))));
  const byGroup = {};
  for (const a of visible) (byGroup[a.group_id] = byGroup[a.group_id] || []).push(a);
  const order = chart.groups.filter(g => byGroup[g.id]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {[[st.groups, 'groups'], [st.active_accounts, 'active accounts'], [st.accounts - st.active_accounts, 'retired'], [st.mapped_accounts, 'with a party mapped']].map(([n, l]) => (
          <div key={l} style={{ ...card, padding: '8px 14px' }}><b style={{ fontSize: 16, color: C.ink }}>{n}</b> <span style={{ fontSize: 12, color: C.muted }}>{l}</span></div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...input, width: 280 }} placeholder="Search code, account or group" value={q} onChange={e => setQ(e.target.value)} />
        <select style={input} value={show} onChange={e => setShow(e.target.value)}>
          <option value="all">All accounts</option>
          <option value="unmapped">Active, nothing mapped</option>
          <option value="retired">Retired</option>
        </select>
        <span style={{ fontSize: 12, color: C.muted }}>{visible.length} of {chart.accounts.length} shown</span>
      </div>
      {!order.length && <div style={{ ...card, fontSize: 12, color: C.muted }}>No account matches this search.</div>}
      {order.map(g => (
        <div key={g.id} style={{ ...card, marginBottom: 10, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '9px 14px', background: '#FAFAFC', borderBottom: `1px solid ${C.line}`, display: 'flex', gap: 10, alignItems: 'center' }}>
            <b style={{ color: C.ink }}>{g.name}</b>
            <span style={chip(C.brandBg, C.brand)}>{g.nature}</span>
            <span style={{ fontSize: 11, color: C.muted }}>{byGroup[g.id].length} account{byGroup[g.id].length === 1 ? '' : 's'}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={{ ...th, width: 140 }}>Code</th><th style={th}>Account</th><th style={{ ...th, width: 110 }}>Status</th><th style={{ ...th, width: 240 }}>Mapped to it</th></tr></thead>
              <tbody>
                {byGroup[g.id].map(a => (
                  <tr key={a.id}>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{a.code}</td>
                    <td style={td}>{a.name}</td>
                    <td style={td}>{a.is_active ? <span style={chip(C.okBg, C.ok)}>Active</span>
                      : <span style={chip('#eee', '#666')} title="The last whole-chart import did not list it. Never deleted.">Retired</span>}</td>
                    <td style={td}>{a.mapped_count ? `${a.mapped_count} (${a.mapped_kinds})` : <span style={{ color: C.muted }}>Nothing mapped yet</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div style={{ fontSize: 11, color: C.muted }}>Groups are shown one level deep: the importer builds each group from the Parent Group column and does not yet nest groups inside groups.</div>
    </div>
  );
}

// ── IMPORT ───────────────────────────────────────────────────────────────────────────────────
const STATUS = {
  validated: ['Checked — ready to apply', C.todoBg, C.todo],
  rejected: ['Rows in error — nothing applied', C.badBg, C.bad],
  applied: ['Applied', C.okBg, C.ok],
  uploaded: ['Not yet checked', '#eee', '#555'],
};
const ACTION = {
  insert: ['New account', C.okBg, C.ok], rename: ['Rename', C.todoBg, C.todo], regroup: ['Moves group', C.todoBg, C.todo],
  unchanged: ['No change', '#f1f1f1', '#666'], error: ['Cannot load', C.badBg, C.bad], pending: ['Not checked', '#eee', '#555'],
};

function ImportView({ H, company, bookName, canChange, applyRefusal, onApplied, hasChart }) {
  const [list, setList] = useState(null);
  const [listErr, setListErr] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    setListErr(null);
    api.get('/finance/accounting/imports', H()).then(r => setList(r.data.data))
      .catch(e => { setList([]); setListErr(`Could not list the uploads: ${errText(e)}`); });
  }, [H]);
  useEffect(() => { setOpen(null); load(); }, [load, company]);

  return (
    <div>
      {canChange
        ? <UploadForm H={H} bookName={bookName} hasChart={hasChart} onDone={(d) => { load(); setOpen(d); }} />
        : <Notice>Uploading and applying a chart is done by accounts or admin.</Notice>}
      {listErr && <Notice tone="bad">{listErr}</Notice>}
      {open && <BatchDetail H={H} detail={open} canChange={canChange} applyRefusal={applyRefusal} bookName={bookName}
        onChanged={(d) => { setOpen(d); load(); if (d?.batch?.status === 'applied') onApplied(); }} onClose={() => setOpen(null)} />}
      <div style={{ ...card, marginTop: 14 }}>
        <div style={{ fontWeight: 700, color: C.ink, marginBottom: 8 }}>Uploads for {bookName}</div>
        {list === null ? <div style={{ fontSize: 12, color: C.muted }}>Reading…</div>
          : !list.length ? <div style={{ fontSize: 12, color: C.muted }}>No chart has been uploaded for {bookName} yet. Every upload is kept here, including ones that were not applied.</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['File', 'Kind', 'Status', 'Rows', 'Errors', 'Uploaded', 'Applied', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>{list.map(b => (
                  <tr key={b.id}>
                    <td style={td}>{b.filename}</td>
                    <td style={td}>{b.is_full_chart ? 'Whole chart' : 'Partial'}</td>
                    <td style={td}><span style={chip(STATUS[b.status][1], STATUS[b.status][2])}>{STATUS[b.status][0]}</span></td>
                    <td style={td}>{b.row_count}</td>
                    <td style={{ ...td, color: b.error_count ? C.bad : C.muted }}>{b.error_count}</td>
                    <td style={td}>{fmtDT(b.uploaded_at)}<br /><span style={{ color: C.muted }}>{b.uploaded_by_name}</span></td>
                    <td style={td}>{b.applied_at ? <>{fmtDT(b.applied_at)}<br /><span style={{ color: C.muted }}>{b.applied_by_name}</span></> : '—'}</td>
                    <td style={td}><button style={btnGhost} onClick={() =>
                      api.get(`/finance/accounting/imports/${b.id}`, H()).then(r => setOpen(r.data))
                        .catch(e => setListErr(`Could not open that upload: ${errText(e)}`))}>Open</button></td>
                  </tr>))}
                </tbody>
              </table>
            </div>)}
      </div>
    </div>
  );
}

function UploadForm({ H, bookName, hasChart, onDone }) {
  const [file, setFile] = useState(null);
  const [full, setFull] = useState(null);   // NO DEFAULT — the person states which it is (mig 342)
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const blocker = !file ? 'Choose the file first.' : full === null ? 'Say whether this is the whole chart or a partial addition.' : null;

  const send = async () => {
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('is_full_chart', String(full));
      const r = await api.post('/finance/accounting/imports', fd, { headers: { ...H().headers, 'Content-Type': 'multipart/form-data' } });
      setFile(null); setFull(null);
      onDone(r.data);
    } catch (e) {
      setMsg(errText(e));
    } finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <div style={{ fontWeight: 700, color: C.ink, marginBottom: 4 }}>Upload a chart for {bookName}</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        The file is stored and every row is checked. Nothing changes in the chart until you open the result and apply it.
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <input type="file" accept=".csv,.xlsx,.xls" onChange={e => { setFile(e.target.files[0] || null); setMsg(null); }} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>CSV or Excel, first sheet, one header row.</div>
        </div>
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>This file is…</div>
          <label style={{ display: 'block', marginBottom: 4 }}>
            <input type="radio" name="full" checked={full === true} onChange={() => setFull(true)} /> <b>the whole chart</b> — any account already loaded that this file does not list is retired (never deleted)
          </label>
          <label style={{ display: 'block' }}>
            <input type="radio" name="full" checked={full === false} onChange={() => setFull(false)} /> <b>a partial addition</b> — nothing already loaded is retired
          </label>
          {!hasChart && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>With no chart loaded yet, the two give the same result; the choice matters from the second upload on.</div>}
        </div>
      </div>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center' }}>
        <button style={blocker || busy ? off(btn) : btn} disabled={!!blocker || busy} onClick={send}>{busy ? 'Uploading and checking…' : 'Upload and check'}</button>
        {blocker && !busy && <span style={why}>{blocker}</span>}
      </div>
      {msg && <div style={{ marginTop: 10 }}><Notice tone="bad"><b>The file was not accepted, and nothing was stored.</b><br />{msg}</Notice></div>}
    </div>
  );
}

function BatchDetail({ H, detail, canChange, applyRefusal, bookName, onChanged, onClose }) {
  const { batch, rows, counts, would_retire: retire } = detail;
  const [showAll, setShowAll] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const errors = rows.filter(r => r.action === 'error');
  const changes = rows.filter(r => r.action === 'rename' || r.action === 'regroup');
  const shown = showAll ? rows : errors;

  const applyBlocker = !canChange ? 'Accounts or admin applies a chart.'
    : batch.status === 'applied' ? `Already applied ${fmtDT(batch.applied_at)} by ${batch.applied_by_name}.`
    : batch.status === 'rejected' ? `${errors.length} row${errors.length === 1 ? '' : 's'} cannot be loaded. Nothing is applied from a file with errors — fix them in the file and upload it again.`
    : batch.status !== 'validated' ? 'This upload has not been checked.'
    // A chart is applied by a PERSON (decision c, mig 357). Named before the click, not after.
    : applyRefusal || null;

  const apply = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post(`/finance/accounting/imports/${batch.id}/apply`, { confirm: true }, H());
      setConfirming(false); onChanged(r.data);
    } catch (e) { setMsg(errText(e)); setConfirming(false); }
    finally { setBusy(false); }
  };

  const st = STATUS[batch.status];
  return (
    <div style={{ ...card, marginTop: 14, borderColor: st[2] }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, color: C.ink }}>{batch.filename} <span style={chip(st[1], st[2])}>{st[0]}</span></div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            {batch.is_full_chart ? 'Whole chart' : 'Partial addition'} · uploaded {fmtDT(batch.uploaded_at)} by {batch.uploaded_by_name}
            {batch.uploaded_by_kind === 'shared' ? ' (a shared login — no one person is recorded)' : ''}
          </div>
        </div>
        <button style={btnGhost} onClick={onClose}>Close</button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
        {['insert', 'rename', 'regroup', 'unchanged', 'error'].map(k => (
          <span key={k} style={chip(ACTION[k][1], ACTION[k][2])}>{ACTION[k][0]}: {counts[k] || 0}</span>))}
        {batch.is_full_chart && batch.status === 'validated' && <span style={chip(retire ? C.todoBg : '#f1f1f1', retire ? C.todo : '#666')}>Would retire: {retire}</span>}
      </div>

      {batch.status === 'rejected' && (
        <Notice tone="bad"><b>{errors.length} of {rows.length} rows cannot be loaded, so nothing from this file has been applied.</b> Each one is listed below with its reason and its row number in the file. Correct the file and upload it again — a partial chart is not loaded, because it would look complete.</Notice>)}
      {batch.status === 'validated' && changes.length > 0 && (
        <Notice tone="todo"><b>{changes.length} existing account{changes.length === 1 ? '' : 's'} will change.</b> Renames and group moves are applied, never silently — each is listed below. Use "Show every row" to review them.</Notice>)}

      {(errors.length > 0 || showAll) && (
        <div style={{ overflowX: 'auto', maxHeight: 460, overflowY: 'auto', border: `1px solid ${C.line}`, borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: 'white' }}><tr>{['Row', 'Ledger name', 'Code', 'Parent group', 'Nature', 'Result', 'Reason / change'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{shown.map(r => (
              <tr key={r.id} style={{ background: r.action === 'error' ? '#FFFAFA' : undefined }}>
                <td style={{ ...td, fontFamily: 'monospace' }}>{r.row_no}</td>
                <td style={td}>{r.ledger_name || <i style={{ color: C.muted }}>blank</i>}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{r.account_code || <i style={{ color: C.muted }}>blank</i>}</td>
                <td style={td}>{r.parent_group || <i style={{ color: C.muted }}>blank</i>}</td>
                <td style={td}>{r.nature || <i style={{ color: C.muted }}>blank</i>}</td>
                <td style={td}><span style={chip(ACTION[r.action][1], ACTION[r.action][2])}>{ACTION[r.action][0]}</span></td>
                <td style={{ ...td, color: r.action === 'error' ? C.bad : '#444' }}>
                  {r.action === 'error' ? r.error
                    : r.action === 'rename' ? <>“{r.current_name}” becomes “{r.ledger_name}”</>
                    : r.action === 'regroup' ? <>moves from {r.current_group} to {r.parent_group}</> : ''}
                </td>
              </tr>))}
            </tbody>
          </table>
        </div>)}
      <div style={{ marginTop: 8 }}>
        <button style={btnGhost} onClick={() => setShowAll(s => !s)}>{showAll ? 'Show only rows that cannot be loaded' : `Show every row (${rows.length})`}</button>
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
        {!confirming ? (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <button style={applyBlocker ? off(btn) : btn} disabled={!!applyBlocker} onClick={() => setConfirming(true)}>Apply to {bookName}'s chart…</button>
            {applyBlocker && <span style={why}>{applyBlocker}</span>}
          </div>
        ) : (
          <Notice tone="todo">
            <b>Apply this file to {bookName}'s chart?</b> It adds {counts.insert || 0}, renames {counts.rename || 0}, moves {counts.regroup || 0} between groups
            {batch.is_full_chart ? <>, and retires {retire} account{retire === 1 ? '' : 's'} the file does not list</> : ', and retires nothing (partial addition)'}.
            Accounts are never deleted. This is recorded against your name.
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button style={busy ? off(btn) : btn} disabled={busy} onClick={apply}>{busy ? 'Applying…' : 'Yes, apply it'}</button>
              <button style={btnGhost} disabled={busy} onClick={() => setConfirming(false)}>Not yet</button>
            </div>
          </Notice>
        )}
        {msg && <Notice tone="bad"><b>Not applied.</b> {msg}</Notice>}
        {batch.status === 'applied' && <Notice tone="ok">Applied {fmtDT(batch.applied_at)} by {batch.applied_by_name}{batch.applied_by_kind === 'shared' ? ' (a shared login — no one person is recorded)' : ''}. {batch.note}</Notice>}
      </div>
    </div>
  );
}

// ── MAPPING ──────────────────────────────────────────────────────────────────────────────────
const KIND_LABEL = { supplier: 'Suppliers', payee: 'Payees', buyer: 'Buyers', deroi_vendor: 'Deroi vendors' };

function MappingView({ H, company, bookName, canChange, goImport }) {
  const [kind, setKind] = useState('supplier');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [show, setShow] = useState('todo');
  const [usedOnly, setUsedOnly] = useState(true);
  const [rowMsg, setRowMsg] = useState({});

  const load = useCallback(() => {
    setErr(null);
    api.get(`/finance/accounting/mapping?kind=${kind}`, H()).then(r => setData(r.data))
      .catch(e => { setData(null); setErr(`Could not read the ${KIND_LABEL[kind].toLowerCase()}: ${errText(e)}`); });
  }, [kind, H]);
  useEffect(() => { load(); }, [load, company]);

  const save = async (s, accountId) => {
    setRowMsg(m => ({ ...m, [s.id]: null }));
    try {
      if (accountId) await api.put('/finance/accounting/mapping', { subject_kind: kind, subject_id: s.id, account_id: accountId }, H());
      else await api.delete(`/finance/accounting/mapping/${kind}/${s.id}`, H());
      load();
    } catch (e) { setRowMsg(m => ({ ...m, [s.id]: errText(e) })); }
  };

  const noAccounts = data && !data.accounts.length;
  const mapBlocker = !canChange ? 'Accounts or admin maps parties.' : noAccounts ? 'Load the chart first — there are no accounts to map to.' : null;
  // A payee linked to a supplier or Deroi vendor is not a to-do of its own: it takes the master's
  // account (decision e). "Mapped" for it means the master is mapped in this book.
  const isMapped = (s) => !!s.account_id || !!s.inherited_account_id;
  const isTodo = (s) => !s.account_id && !s.linked_kind;
  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.data.filter(s =>
      (show === 'all' || (show === 'todo' ? isTodo(s) : show === 'linked' ? !!s.linked_kind : isMapped(s)))
      && (kind !== 'payee' || !usedOnly || s.used_here > 0)
      && (!needle || [s.name, s.gstin, s.linked_master, s.account_name].some(x => String(x || '').toLowerCase().includes(needle))));
  }, [data, q, show, usedOnly, kind]);
  const hiddenUnused = data && kind === 'payee' && usedOnly ? data.data.filter(s => !(s.used_here > 0)).length : 0;
  const sections = kind === 'supplier'
    ? [[`Suppliers of ${bookName}`, rows.filter(r => r.company_recorded), null],
       ['Suppliers with no company recorded', rows.filter(r => !r.company_recorded),
        `The supplier master holds no company for these, so they are listed in both books. Mapping one here maps it in ${bookName}'s book only; Deroi's mapping is separate.`]]
    : [[KIND_LABEL[kind], rows, null]];

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
        {Object.entries(KIND_LABEL).map(([k, l]) => (
          <button key={k} onClick={() => { setKind(k); setData(null); }} style={{ ...(kind === k ? btn : btnGhost), padding: '6px 12px' }}>{l}</button>))}
      </div>
      {err && <Notice tone="bad">{err}</Notice>}
      {!data ? (!err && <div style={{ ...card, fontSize: 12, color: C.muted }}>Reading…</div>) : (
        <>
          {noAccounts && (
            <Notice tone="todo">
              <b>No chart is loaded for {bookName}, so there is nothing to map to yet.</b> The list below is the work waiting for it:
              every party here will need a ledger account once the chart is in.{' '}
              {canChange && <button style={{ ...btnGhost, padding: '3px 10px' }} onClick={goImport}>Go to Import</button>}
            </Notice>)}
          {data.note && <Notice>{data.note}</Notice>}
          {!data.note && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={chip(C.todoBg, C.todo)}>To map: {data.counts.to_map}</span>
              <span style={chip(C.okBg, C.ok)}>Mapped: {data.counts.mapped}</span>
              {kind === 'payee' && <span style={chip(C.brandBg, C.brand)} title="Linked to a supplier or Deroi vendor: they take that party's account and are not mapped on their own.">
                Through their supplier/vendor: {data.counts.linked} ({data.counts.inherited} of them mapped)</span>}
              <span style={{ fontSize: 12, color: C.muted }}>of {data.counts.total} {KIND_LABEL[kind].toLowerCase()}</span>
              <input style={{ ...input, width: 240 }} placeholder="Search" value={q} onChange={e => setQ(e.target.value)} />
              <select style={input} value={show} onChange={e => setShow(e.target.value)}>
                <option value="todo">To map</option><option value="mapped">Mapped</option>
                {kind === 'payee' && <option value="linked">Through their supplier/vendor</option>}<option value="all">All</option>
              </select>
              {kind === 'payee' && (
                <label style={{ fontSize: 12 }}><input type="checkbox" checked={usedOnly} onChange={e => setUsedOnly(e.target.checked)} /> Only payees used in {bookName}'s documents
                  {hiddenUnused > 0 && <span style={{ color: C.muted }}> ({hiddenUnused} unused hidden)</span>}</label>)}
            </div>)}
          {!data.note && sections.map(([title, list, note]) => (
            <div key={title} style={{ ...card, padding: 0, marginBottom: 12, overflow: 'hidden' }}>
              <div style={{ padding: '9px 14px', background: '#FAFAFC', borderBottom: `1px solid ${C.line}` }}>
                <b style={{ color: C.ink }}>{title}</b> <span style={{ fontSize: 11, color: C.muted }}>({list.length} shown)</span>
                {note && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{note}</div>}
              </div>
              {!list.length ? <div style={{ padding: 14, fontSize: 12, color: C.muted }}>
                {show === 'todo' ? 'Nothing left to map here.' : 'Nothing matches.'}</div> : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={th}>Party</th>
                      {kind === 'payee' ? <><th style={th}>Same party as</th><th style={th}>Documents here</th></> : <th style={th}>GSTIN</th>}
                      <th style={{ ...th, width: 340 }}>Ledger account</th>
                    </tr></thead>
                    <tbody>{list.map(s => (
                      <tr key={s.id}>
                        <td style={td}>{s.name}
                          {s.intercompany_with && (
                            <div style={{ marginTop: 4 }}>
                              <span style={chip(C.brandBg, C.brand)}>Intercompany</span>
                              <div style={{ fontSize: 11, color: '#444', marginTop: 3, maxWidth: 360 }}>
                                This is {s.intercompany_with}, our own company. What is owed here is a balance between our two books,
                                not an ordinary {kind === 'buyer' ? 'debtor' : 'creditor'}: map it to an intercompany account, not the general
                                {kind === 'buyer' ? ' debtors' : ' creditors'} group, and expect a matching account in {s.intercompany_with}'s book.
                              </div>
                            </div>)}
                        </td>
                        {kind === 'payee'
                          ? <><td style={{ ...td, color: s.linked_master ? '#444' : C.muted }}>{s.linked_master || 'no master linked'}</td><td style={td}>{s.used_here}</td></>
                          : <td style={{ ...td, fontFamily: 'monospace', color: s.gstin ? '#444' : C.muted }}>{s.gstin || '—'}</td>}
                        <td style={td}>
                          {s.linked_kind ? (
                            // Decision e: one party, one account. No picker — the master is mapped instead.
                            <div style={{ fontSize: 12 }}>
                              Takes {s.linked_kind === 'supplier' ? 'supplier' : 'Deroi vendor'} <b>{s.linked_master}</b>'s account:{' '}
                              {s.inherited_account_id ? <span>{s.inherited_account_code} · {s.inherited_account_name}</span>
                                : <span style={chip(C.todoBg, C.todo)}>not mapped yet</span>}
                              <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                                A payee linked to a {s.linked_kind === 'supplier' ? 'supplier' : 'Deroi vendor'} is the same party, so it is not mapped on its own.
                                {!s.inherited_account_id && ` Map it under ${s.linked_kind === 'supplier' ? 'Suppliers' : 'Deroi vendors'}.`}
                              </div>
                              {s.account_id && <div style={{ fontSize: 11, color: C.bad, marginTop: 3 }}>
                                It also has its own mapping ({s.account_code} · {s.account_name}) from before it was linked — two accounts for one party. Unmap it:{' '}
                                <button style={{ ...btnGhost, padding: '2px 8px' }} onClick={() => save(s, null)}>Unmap</button></div>}
                            </div>
                          ) : mapBlocker ? (
                            s.account_id ? <span>{s.account_code} · {s.account_name}</span>
                              : <span><span style={chip(C.todoBg, C.todo)}>To map</span><span style={why}>{mapBlocker}</span></span>
                          ) : (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <select style={{ ...input, flex: 1, maxWidth: 260, borderColor: s.account_id ? '#ddd' : C.todo }}
                                value={s.account_id || ''} onChange={e => e.target.value && save(s, e.target.value)}>
                                <option value="">— to map —</option>
                                {s.account_id && s.account_active === false && <option value={s.account_id}>{s.account_code} · {s.account_name} (retired)</option>}
                                {data.accounts.map(a => <option key={a.id} value={a.id}>{a.name} · {a.code} ({a.group_name})</option>)}
                              </select>
                              {s.account_id && <button style={{ ...btnGhost, padding: '5px 9px' }} onClick={() => save(s, null)} title="Return this party to unmapped">Unmap</button>}
                            </div>)}
                          {s.account_id && s.account_active === false && <div style={{ fontSize: 11, color: C.todo, marginTop: 3 }}>Mapped to a retired account — choose an active one.</div>}
                          {s.account_id && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>by {s.mapped_by_name || '—'} · {fmtDT(s.mapped_at)}</div>}
                          {rowMsg[s.id] && <div style={{ fontSize: 11, color: C.bad, marginTop: 3 }}>Not saved: {rowMsg[s.id]}</div>}
                        </td>
                      </tr>))}
                    </tbody>
                  </table>
                </div>)}
            </div>))}
        </>)}
    </div>
  );
}
